import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/get-involved", label: "Get Involved" },
  { to: "/refer", label: "Refer" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isDashboard = loc.pathname.startsWith("/dashboard") || loc.pathname.startsWith("/login");
  if (isDashboard) return null;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-colors ${
        scrolled ? "bg-njas-bg/90 backdrop-blur-md" : "bg-njas-bg"
      } border-b border-[var(--njas-line)]`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-[76px]">
        <Link to="/" data-testid="header-home-link" onClick={() => setOpen(false)} className="flex items-center">
          <Logo variant="compact" />
        </Link>

        <nav className="hidden lg:flex items-center">
          {nav.map((n, i) => (
            <React.Fragment key={n.to}>
              <NavLink
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `njas-condensed text-[13px] tracking-[0.2em] px-4 py-2 font-semibold transition-colors ${
                    isActive ? "text-njas-navy" : "text-njas-muted hover:text-njas-navy"
                  }`
                }
              >
                {n.label}
              </NavLink>
              {i < nav.length - 1 && <span className="w-px h-3 bg-[var(--njas-line-strong)] opacity-30" />}
            </React.Fragment>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user && user.id ? (
            <Link
              to="/dashboard"
              data-testid="header-dashboard-link"
              className="njas-condensed text-[12px] tracking-[0.22em] font-semibold px-4 py-2.5 text-njas-navy hover:text-njas-sage-deep"
            >
              Staff Portal ↗
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="header-login-link"
              className="njas-condensed text-[12px] tracking-[0.22em] font-semibold px-4 py-2.5 text-njas-navy hover:text-njas-sage-deep"
            >
              Staff Login
            </Link>
          )}
          <Link
            to="/refer"
            data-testid="header-refer-cta"
            className="njas-condensed text-[13px] tracking-[0.18em] font-semibold px-5 py-3 bg-njas-navy text-njas-bg hover:bg-njas-navy-hover transition-colors"
          >
            Make a Referral
          </Link>
        </div>

        <button
          className="lg:hidden p-2 -mr-2 text-njas-navy"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--njas-line)] bg-njas-bg" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `njas-condensed tracking-[0.2em] font-semibold text-sm py-4 border-b border-[var(--njas-line)] ${
                    isActive ? "text-njas-navy" : "text-njas-muted"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="pt-4 flex flex-col gap-2">
              {user && user.id ? (
                <Link to="/dashboard" onClick={() => setOpen(false)} className="njas-condensed tracking-[0.2em] font-semibold py-3 border border-njas-navy text-njas-navy text-center">
                  Staff Portal
                </Link>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="njas-condensed tracking-[0.2em] font-semibold py-3 border border-njas-navy text-njas-navy text-center">
                  Staff Login
                </Link>
              )}
              <Link to="/refer" onClick={() => setOpen(false)} className="njas-condensed tracking-[0.2em] font-semibold py-3 bg-njas-navy text-njas-bg text-center">
                Make a Referral
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
